import type { RateLimitSource } from '@nadoutong/contracts';

export interface LayerCaps {
  readonly rpm: number | null;
  readonly tpm: number | null;
  /** #2738 post-settled uncached-input TPM. Absent = not set on this layer. */
  readonly tpmUncached?: number | null;
}

export interface EffectiveLayers {
  readonly orgSku: LayerCaps | null;
  readonly orgAll: LayerCaps | null;
  readonly sku: LayerCaps | null;
  readonly global: LayerCaps | null;
}

export interface EffectiveCap {
  readonly value: number | null;
  readonly source: RateLimitSource;
}

export interface EffectivePair {
  readonly rpm: EffectiveCap;
  readonly tpm: EffectiveCap;
  readonly tpmUncached: EffectiveCap;
}

type Dim = 'rpm' | 'tpm' | 'tpmUncached';

function dimOf(layer: LayerCaps | null, dim: Dim): number | null {
  if (layer === null) return null;
  return layer[dim] ?? null;
}

function pick(layers: EffectiveLayers, dim: Dim): EffectiveCap {
  const orgSku = dimOf(layers.orgSku, dim);
  if (orgSku !== null) return { value: orgSku, source: 'org_sku' };
  const orgAll = dimOf(layers.orgAll, dim);
  if (orgAll !== null) return { value: orgAll, source: 'org_all' };
  const sku = dimOf(layers.sku, dim);
  if (sku !== null) return { value: sku, source: 'sku' };
  const global = dimOf(layers.global, dim);
  if (global !== null) return { value: global, source: 'global' };
  return { value: null, source: 'none' };
}

/** Four-layer RPM/TPM/uncached-TPM resolver. Layers are independent per dimension. */
export function resolveEffective(layers: EffectiveLayers): EffectivePair {
  return {
    rpm: pick(layers, 'rpm'),
    tpm: pick(layers, 'tpm'),
    tpmUncached: pick(layers, 'tpmUncached'),
  };
}
